import CheckOutlined from '@mui/icons-material/CheckOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import { Box, Chip, ChipProps, Tooltip, Typography, useTheme } from '@mui/material';
import React from 'react';

type StatusVariant = 'complete' | 'incomplete';

interface Props {
  label: string;
  variant?: StatusVariant;
  color?: ChipProps['color'];
  icon?: React.ReactElement;
  iconOnly?: boolean;
  tooltip?: string;
}

const StatusChip: React.FC<Props> = ({ label, variant, color, icon, iconOnly, tooltip }) => {
  const theme = useTheme();

  const isComplete = variant === 'complete';
  const VariantIcon = isComplete ? CheckOutlined : CloseOutlined;
  const variantIconColor = isComplete ? theme.palette.success.main : theme.palette.error.main;
  const resolvedColor = variant !== undefined ? (isComplete ? 'success' : 'error') : color;
  const resolvedIcon = variant !== undefined ? <VariantIcon sx={{ fill: variantIconColor }} /> : icon;
  const labelColor =
    resolvedColor === 'success' || resolvedColor === 'error' ? theme.palette[resolvedColor].light : undefined;

  const collapsedStyles = {
    '& .MuiChip-label': { display: 'none', padding: 0, color: labelColor },
    '& .MuiChip-icon': { margin: 0 },
    borderRadius: '50%',
    padding: '3px'
  };

  const chip = (
    <Chip
      variant="filled"
      size="small"
      color={resolvedColor}
      icon={resolvedIcon}
      label={<Typography variant="caption">{label}</Typography>}
      sx={
        iconOnly
          ? collapsedStyles
          : {
              '& .MuiChip-label': { color: labelColor },
              [theme.containerQueries.down(80)]: collapsedStyles
            }
      }
    />
  );

  return (
    <Box
      sx={{
        containerType: 'inline-size',
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: iconOnly ? 'center' : 'flex-start',
        alignItems: 'center'
      }}
    >
      {tooltip ? (
        <Tooltip
          title={
            <Typography variant="caption" sx={{ whiteSpace: 'pre-line' }}>
              {tooltip}
            </Typography>
          }
        >
          {chip}
        </Tooltip>
      ) : (
        chip
      )}
    </Box>
  );
};

export default StatusChip;
